import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { technicalAPI, oltAPI, nasAPI } from '../../services/api';
import { SeverityBadge, LoadingBlock, EmptyState, CARD } from '../../components/technical/TechnicalUI';
import { navItems } from '../../components/technical/technicalNavConfig';
import { useAuth } from '../../context/AuthContext';
import LinkHealthCard from '../../components/technical/LinkHealthPanel';
import OnuManagerPanel from '../../components/technical/OnuManagerPanel';
import {
  AlertTriangle, Gauge, Zap, CheckCircle2,
  Bell, ChevronRight, ArrowDown, ArrowUp, Search,
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import useDarkReady from '../../theme/useDarkReady';

// Same match predicate as TechnicalOnuListPage's own search, so "1 result" here really does
// mean the ONU List would show exactly one row for the same query.
const matchesOnu = (o, q) => `${o.id} ${o.label || ''} ${o.description || ''} ${o.serialNumber || ''}`.toLowerCase().includes(q);

function OnuSearchBox() {
  const [q, setQ] = useState('');
  const [searching, setSearching] = useState(false);
  const navigate = useNavigate();

  const runSearch = async (e) => {
    e.preventDefault();
    const query = q.trim();
    if (!query) return;
    setSearching(true);
    try {
      const { data } = await oltAPI.getONUs();
      const matches = (data.data || []).filter(o => matchesOnu(o, query.toLowerCase()));
      if (matches.length === 1) {
        const o = matches[0];
        navigate(`/technical/onus/${o.slot}/${o.port}/${o.onuId}`);
      } else if (matches.length === 0) {
        toast.error(`No ONU found matching "${query}"`);
      } else {
        // Ambiguous — hand off to the full list, pre-filtered, instead of guessing.
        navigate(`/technical/onus?q=${encodeURIComponent(query)}`);
      }
    } catch {
      toast.error('Could not reach OLT');
    } finally {
      setSearching(false);
    }
  };

  return (
    <form onSubmit={runSearch} style={{ position: 'relative', marginBottom: 10 }}>
      <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
      <input
        className="form-control"
        style={{ paddingLeft: 36, height: 40, fontSize: '.9rem' }}
        placeholder="Search ONU id, label, serial…"
        value={q}
        onChange={e => setQ(e.target.value)}
        disabled={searching}
      />
    </form>
  );
}

// The page opens on this hub: one tap to any network tool, live counts on the ones that have them.
// It renders before the slow OLT dashboard call returns, so the page is usable immediately.
const TILE_COLOR = {
  'olt-status': '#1e40af', onus: '#0d9488', alarms: '#d03b3b', 'pon-overview': '#7c3aed', 'network-map': '#0ea5e9',
  connections: '#16a34a', complaints: '#ea580c', 'fiber-map': '#64748b', olt: '#64748b', acs: '#64748b', speedtest: '#7c3aed',
};
const ADMIN_ONLY = ['fiber-map', 'olt', 'acs'];

function ShortcutGrid({ counts }) {
  const { user } = useAuth();
  const isTech = user?.role === 'technical';
  const tiles = [
    ...navItems.filter(n => n.id !== 'dashboard' && !(isTech && ADMIN_ONLY.includes(n.id))),
    { id: 'speedtest', to: '/technical/speedtest', icon: Zap, label: 'Speed Test' },
  ];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 14 }}>
      {tiles.map(t => {
        const color = TILE_COLOR[t.id] || '#1e40af';
        const n = counts?.[t.id];
        return (
          <Link key={t.id} to={t.to} style={{
            position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6,
            minHeight: 68, padding: '10px 4px', borderRadius: 14, textDecoration: 'none',
            background: `${color}10`, border: `1px solid ${color}22`,
          }}>
            <t.icon size={20} color={color} />
            <span style={{ fontSize: '.72rem', fontWeight: 600, color: 'var(--text)', textAlign: 'center', lineHeight: 1.15 }}>
              {t.label.replace(' (Admin)', '')}
            </span>
            {n > 0 && (
              <span style={{ position: 'absolute', top: 6, right: 8, minWidth: 18, height: 18, padding: '0 5px', borderRadius: 9, background: color, color: '#fff', fontSize: '.66rem', fontWeight: 700, display: 'grid', placeItems: 'center' }}>{n}</span>
            )}
          </Link>
        );
      })}
    </div>
  );
}

function fmtMbps(v) {
  if (v == null) return '—';
  return v >= 100 ? v.toFixed(0) : v >= 10 ? v.toFixed(1) : v.toFixed(2);
}

const fmtClock = (d) => d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });

// Fixed-length ring buffer, same technique as the admin Dashboard's NASUplinkBandwidthPanel:
// MAX_SECS slots exist from the very first render, and every tick shifts one out on the left
// and pushes one in on the right, so the point count — and each point's x-position, since
// recharts' default category axis divides the chart width by however many entries exist —
// never changes. The line stays anchored to "now" on the right edge and scrolls left,
// instead of visibly compressing/rescaling every tick.
//
// Slots are seeded with real back-dated clock labels and null values, so the axis reads
// correctly from the first render — those seconds genuinely did happen, we just have no
// samples for them until the page has been open that long. No range picker anymore — this
// chart is always the live last-minute window, polled at 1s, full stop.
const MAX_SECS = 60;

const emptyBuffer = () => {
  const now = Date.now();
  return Array.from({ length: MAX_SECS }, (_, i) => ({
    time: fmtClock(new Date(now - (MAX_SECS - 1 - i) * 1000)),
    rx: null,
    tx: null,
  }));
};

export default function TechnicalDashboardPage() {
  useDarkReady();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bw, setBw] = useState({ configured: false, warming: true, current: null });
  const [history, setHistory] = useState(emptyBuffer);
  const [syncing, setSyncing] = useState(false);
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  const loadDashboard = () => technicalAPI.getDashboard().then(({ data }) => { if (mountedRef.current) setData(data.data); }).catch(() => {}).finally(() => mountedRef.current && setLoading(false));

  // Full dashboard (OLT scrape + alarms + user stats) is too heavy to poll every second —
  // refreshed on a slower cadence. The live bandwidth gauge reuses the same NAS uplink
  // endpoint the admin Dashboard's "Bandwidth Flow" panel uses (nasAPI /nas/uplink-bandwidth
  // — the Ellora↔CTPL backhaul, see nasUplinkService.js), polled at 1s.
  useEffect(() => {
    loadDashboard();
    const t = setInterval(loadDashboard, 15000);
    return () => clearInterval(t);
  }, []);

  // Manual "Sync OLT" — forces a fresh scrape past the 2-min ONU cache (oltService.js's
  // getONUs(force)) instead of waiting for the automatic 15s poll to eventually land on a
  // stale-cache window. getFiberView() (which the dashboard's userStats/olt data come from)
  // reads the same shared cache, so re-fetching the dashboard right after picks up the fresh scrape.
  const handleSync = async () => {
    setSyncing(true);
    // A full scrape past the cache measured ~80s against this OLT — tooltips don't help on
    // this phone-first page, so say it up front rather than let the spinner look stuck.
    toast('Syncing OLT… can take up to a minute', { icon: '⏳' });
    try {
      await oltAPI.getONUs({ refresh: true });
      await loadDashboard();
      toast.success('OLT synced');
    } catch (err) {
      if (err.response) {
        // A real response came back (bad permissions, OLT actually unreachable, etc.) —
        // this is a genuine failure, surface the server's own message.
        toast.error(err.response.data?.message || 'Sync failed');
      } else {
        // No response at all — on a mobile connection an ~80s open request is fragile
        // (carrier NAT/middlebox can drop an idle-looking connection even with the screen
        // on). The route has no client-disconnect handling, so the scrape almost certainly
        // kept running server-side and updated the shared cache anyway — re-fetch instead
        // of reporting a failure that likely isn't real.
        toast('Connection dropped, but the sync may have completed — checking…', { icon: '🔄' });
        try { await loadDashboard(); } catch {}
      }
    } finally {
      if (mountedRef.current) setSyncing(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    const load = () => {
      nasAPI.getUplinkBandwidth().then(({ data }) => {
        if (!mounted) return;
        setBw(data);
        if (!data.warming && data.current) {
          const { rx, tx } = data.current;
          setHistory(h => [...h.slice(1), { time: fmtClock(new Date()), rx, tx }]);
        }
      }).catch(() => {});
    };
    load();
    const t = setInterval(load, 1000);
    return () => { mounted = false; clearInterval(t); };
  }, []);

  const hub = (counts) => (<><OnuSearchBox /><ShortcutGrid counts={counts} /></>);
  if (loading) return <div>{hub()}<LoadingBlock /></div>;
  if (!data) return <div>{hub()}<EmptyState icon={AlertTriangle} text="Failed to load dashboard" /></div>;

  const { userStats, olt, alarms, openTickets } = data;
  const counts = { alarms: alarms.counts.actionable, complaints: openTickets };

  return (
    <div>
      {hub(counts)}

      {/* The page title and date live inside the health card now — one framed block at the
          top of the screen instead of a bare heading floating above one. */}
      <LinkHealthCard
        health={userStats.linkHealth}
        dateLine={`Today, ${new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}`}
        onSync={handleSync}
        syncing={syncing}
      />

      {olt.configured && olt.reachable && (
        <OnuManagerPanel variant="panel" maxHeight={420} />
      )}

      {!olt.configured && (
        <div style={{ border: '1px solid var(--cb-fde68a)', background: 'var(--cb-fffbeb)', borderRadius: 16, padding: '12px 16px', marginBottom: 20, fontSize: '.85rem', color: 'var(--c-92400e)' }}>
          No OLT configured — set it up in Settings &gt; OLT to see live hardware data here.
        </div>
      )}
      {olt.configured && !olt.reachable && (
        <div style={{ border: '1px solid var(--cb-fecaca)', background: 'var(--cb-fef2f2)', borderRadius: 16, padding: '12px 16px', marginBottom: 20, fontSize: '.85rem', color: 'var(--c-991b1b)' }}>
          OLT unreachable{olt.error ? `: ${olt.error}` : ''}
        </div>
      )}

      <div style={{ ...CARD, padding: '14px 16px 12px', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 14 }}>
          <Gauge size={18} color="var(--primary)" style={{ flexShrink: 0 }} />
          <span style={{ fontWeight: 800, fontSize: '1.02rem', color: 'var(--c-111827)' }}>NAS Uplink Bandwidth</span>
          {/* No range picker — this chart is always the live 1s-polled window, so the badge
              says exactly that instead of offering choices that don't apply. */}
          <span style={{
            marginLeft: 'auto', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 5,
            fontSize: '.72rem', fontWeight: 700, letterSpacing: '.03em', color: 'var(--c-0ca30c)',
            background: '#0ca30c17', borderRadius: 999, padding: '5px 10px',
          }}>
            <span className="live-dot" style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--cb-0ca30c)' }} />
            LIVE 1s
          </span>
        </div>
        {!bw.configured ? (
          <div style={{ color: 'var(--text-muted)', fontSize: '.85rem', paddingBottom: 4 }}>NAS 1 (Ellora) or NAS 2 (AIS_BB_2) not reporting yet.</div>
        ) : bw.current ? (
          <>
            <div style={{ display: 'flex', gap: 24, marginBottom: 10 }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
                  <ArrowDown size={20} color="var(--primary)" style={{ alignSelf: 'center', flexShrink: 0 }} />
                  <span style={{ fontSize: '1.6rem', fontWeight: 800, color: 'var(--primary)', lineHeight: 1, letterSpacing: '-.02em' }}>{fmtMbps(bw.current.rx)}</span>
                  <span style={{ fontSize: '.72rem', fontWeight: 500, color: 'var(--text-muted)' }}>Mbps</span>
                </div>
                <div style={{ fontSize: '.75rem', color: 'var(--c-6b7280)', marginTop: 4 }}>Download</div>
              </div>
              <div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
                  <ArrowUp size={20} color="var(--c-ea580c)" style={{ alignSelf: 'center', flexShrink: 0 }} />
                  <span style={{ fontSize: '1.6rem', fontWeight: 800, color: 'var(--c-ea580c)', lineHeight: 1, letterSpacing: '-.02em' }}>{fmtMbps(bw.current.tx)}</span>
                  <span style={{ fontSize: '.72rem', fontWeight: 500, color: 'var(--text-muted)' }}>Mbps</span>
                </div>
                <div style={{ fontSize: '.75rem', color: 'var(--c-6b7280)', marginTop: 4 }}>Upload</div>
              </div>
            </div>
            <div style={{ height: 110 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={history} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="tdRx" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="var(--primary)" stopOpacity={0.25} />
                      <stop offset="95%" stopColor="var(--primary)" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="tdTx" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="var(--cl-ea580c)" stopOpacity={0.25} />
                      <stop offset="95%" stopColor="var(--cl-ea580c)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="time"
                    tick={{ fontSize: 9, fill: 'var(--text-muted)' }}
                    axisLine={{ stroke: 'var(--border)' }}
                    tickLine={false}
                    minTickGap={40}
                    interval="preserveStartEnd"
                  />
                  <YAxis hide domain={[0, 'auto']} />
                  <Tooltip contentStyle={{ fontSize: '.75rem', borderRadius: 6, padding: '4px 8px' }} formatter={(v, n) => [`${fmtMbps(v)} Mbps`, n === 'rx' ? 'Down' : 'Up']} />
                  <Area type="monotone" dataKey="rx" stroke="var(--primary)" strokeWidth={2} fill="url(#tdRx)" dot={false} isAnimationActive={false} connectNulls={false} />
                  <Area type="monotone" dataKey="tx" stroke="var(--cl-ea580c)" strokeWidth={2} fill="url(#tdTx)" dot={false} isAnimationActive={false} connectNulls={false} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </>
        ) : (
          <div style={{ color: 'var(--text-muted)', fontSize: '.85rem', paddingBottom: 4 }}>Collecting first data samples…</div>
        )}
      </div>

      {/* Major and critical only. The minor rows — overwhelmingly customers switching their
          own power off — are still recorded and are one tap away behind the Alarms page's
          "Show N minor alarms" toggle; they just don't get to fill the home screen. The
          heading says "Major" rather than "Recent" for that reason: this is a filtered list,
          and calling it recent would read as the whole log. */}
      <div style={{ ...CARD, padding: '14px 16px', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 12 }}>
          <Bell size={18} color="var(--c-111827)" style={{ flexShrink: 0 }} />
          <span style={{ fontWeight: 800, fontSize: '1.02rem', color: 'var(--c-111827)' }}>Major Alarms</span>
          <Link
            to="/technical/alarms"
            style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 2, fontSize: '.8rem', fontWeight: 600, flexShrink: 0 }}
          >
            View All <ChevronRight size={15} />
          </Link>
        </div>
        {alarms.recent.length === 0 ? (
          <div style={{ color: 'var(--text-muted)', fontSize: '.85rem' }}>
            No major or critical alarms.
            {alarms.counts.minor > 0 && (
              <> <Link to="/technical/alarms" style={{ fontSize: '.8rem' }}>{alarms.counts.minor} minor</Link> logged.</>
            )}
          </div>
        ) : (
          <div>
            {/* This list is by recency, so it can include faults that have since cleared.
                Resolved ones are marked — otherwise a fixed fault reads as an open one and
                someone drives out for nothing. */}
            {alarms.recent.map((a, i) => (
              <div
                key={a._id}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, minHeight: 44,
                  padding: '7px 0', fontSize: '.85rem',
                  borderTop: i === 0 ? 'none' : '1px solid var(--cb-f1f2f4)',
                  opacity: a.status === 'resolved' ? .55 : 1,
                }}
              >
                <SeverityBadge severity={a.severity} />
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.message}</span>
                {a.status === 'resolved' && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: '.7rem', color: 'var(--c-0ca30c)', flexShrink: 0 }}>
                    <CheckCircle2 size={11} />Fixed
                  </span>
                )}
                <span style={{ color: 'var(--text-muted)', fontSize: '.75rem', flexShrink: 0 }}>{new Date(a.raisedAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
